import { supabase } from "./supabase";

/** Thin wrappers over Supabase. Every one throws on error so screens can
 *  use try/catch rather than checking a shape each time. */

const ok = ({ data, error }) => { if (error) throw error; return data; };

/* ---------- society ---------- */

/**
 * Creating a society, a profile and an admin membership has to happen
 * together or not at all, and the row level security policies can't
 * express that on their own — you can't be the organiser of a society
 * that doesn't exist yet. So it's one database function.
 */
export const createSociety = (name) =>
  supabase.rpc("create_society", { p_name: name }).single().then(ok);

/** Makes sure the signed-in user has a profile row. Safe to call twice. */
export const ensureProfile = () =>
  supabase.rpc("ensure_profile").then(ok);

/* ---------- members ---------- */

export const listMembers = (societyId) =>
  supabase
    .from("society_members")
    .select("role, joined_at, profiles!inner(id, full_name, email, handicap_index, plays_from, user_id)")
    .eq("society_id", societyId)
    .then(ok);

/**
 * Adding someone is two writes plus the read Postgres does on the way
 * back out, which is three policy checks for one action. One function
 * instead, same as society and course creation.
 */
export const addMember = (societyId, { full_name, email, handicap_index, plays_from, role }) =>
  supabase.rpc("add_member", {
    p_society_id: societyId,
    p_full_name: full_name,
    p_email: email || null,
    p_handicap_index:
      handicap_index === "" || handicap_index == null ? null : Number(handicap_index),
    p_plays_from: plays_from || "any",
    p_role: role || "player",
  }).single().then(ok);

export const updateMember = (societyId, profileId, patch) =>
  supabase.rpc("update_member", {
    p_society_id: societyId,
    p_profile_id: profileId,
    p_full_name: patch.full_name,
    p_email: patch.email || null,
    p_handicap_index:
      patch.handicap_index === "" || patch.handicap_index == null
        ? null : Number(patch.handicap_index),
    p_plays_from: patch.plays_from || "any",
    p_role: patch.role ?? null,
  }).single().then(ok);

export const removeMember = (societyId, profileId) =>
  supabase.rpc("remove_member", {
    p_society_id: societyId, p_profile_id: profileId,
  }).then(ok);


/* ---------- competitions ---------- */

export const listCompetitions = (societyId) =>
  supabase
    .from("competitions")
    .select("*, rounds(id, round_number, name, played_on, status, courses(name))")
    .eq("society_id", societyId)
    .order("created_at", { ascending: false })
    .then(ok);

/* ---------- courses ---------- */

export const listCourses = (societyId) =>
  supabase
    .from("courses")
    .select("*, tee_sets(id, name, gender, par, course_rating, slope_rating)")
    .or(`society_id.eq.${societyId},society_id.is.null`)
    .order("name")
    .then(ok);

export const getCourse = (courseId) =>
  supabase
    .from("courses")
    .select("*, tee_sets(*, tee_holes(hole_number, par, stroke_index))")
    .eq("id", courseId)
    .single()
    .then(ok);

/** Course, tees and all 18 holes in one call — a course with tees but no
 *  stroke indexes would break every nett score on it. */
export const createCourse = (societyId, { name, club, city, tees }) =>
  supabase.rpc("create_course", {
    p_society_id: societyId, p_name: name, p_club: club, p_city: city, p_tees: tees,
  }).single().then(ok);

export const updateCourse = (courseId, { name, club, city }) =>
  supabase.rpc("update_course", {
    p_course_id: courseId, p_name: name, p_club: club, p_city: city,
  }).single().then(ok);

/** Creates a tee set when teeSetId is null, otherwise corrects it.
 *  Holes are replaced wholesale — correcting a stroke index re-derives
 *  every nett and Stableford score on that course, which is the point. */
export const upsertTeeSet = (courseId, teeSetId, t) =>
  supabase.rpc("upsert_tee_set", {
    p_course_id: courseId, p_tee_set_id: teeSetId, p_name: t.name,
    p_gender: t.gender, p_rating: Number(t.course_rating),
    p_slope: Number(t.slope_rating), p_holes: t.holes,
  }).then(ok);

export const deleteTeeSet = (teeSetId) =>
  supabase.rpc("delete_tee_set", { p_tee_set_id: teeSetId }).then(ok);

/* ---------- competition setup ---------- */

export const createCompetition = (societyId, c) =>
  supabase.rpc("create_competition", {
    p_society_id: societyId,
    p_name: c.name,
    p_primary_metric: c.primaryMetric,
    p_allowance: c.allowance,
    p_aggregation: c.aggregation,
    p_counting_rounds: c.countingRounds,
    p_hole_out: c.requiresHoleOut,
    p_rounds: c.rounds,
    p_freeze: c.freezeHandicaps ?? true,
  }).single().then(ok);

/** Enters everyone with a handicap index, freezing it at today's value. */
export const enterSociety = (competitionId) =>
  supabase.rpc("enter_society", { p_competition_id: competitionId }).then(ok);

export const getCompetition = (id) =>
  supabase
    .from("competitions")
    .select("*, rounds(id, round_number, name, played_on, status, courses(id, name))")
    .eq("id", id)
    .single()
    .then(ok);

/* ---------- rounds and scoring ---------- */

export const getRound = (roundId) =>
  supabase
    .from("rounds")
    .select(`*, competitions(*), courses(name),
             round_tees(tee_set_id, default_for,
               tee_sets(id, name, gender, par, course_rating, slope_rating,
                 tee_holes(hole_number, par, stroke_index)))`)
    .eq("id", roundId)
    .single()
    .then(ok);

export const listEntries = (roundId) =>
  supabase
    .from("round_entries")
    .select("id, profile_id, card_status, playing_handicap, course_handicap, tee_set_id, profiles!round_entries_profile_id_fkey(full_name), tee_sets(name)")
    .eq("round_id", roundId)
    .then(ok);

/** Puts the field on their tees and opens the round. Safe to repeat. */
export const openRound = (roundId) =>
  supabase.rpc("open_round", { p_round_id: roundId }).then(ok);

export const getEntry = (entryId) =>
  supabase
    .from("round_entries")
    .select(`id, round_id, profile_id, card_status, playing_handicap, course_handicap,
             handicap_index, allowance_applied,
             profiles!round_entries_profile_id_fkey(full_name),
             tee_sets(id, name, par, course_rating, slope_rating,
               tee_holes(hole_number, par, stroke_index)),
             rounds(id, round_number, name, played_on, status, competition_id,
               courses(name), competitions(name, primary_metric, requires_hole_out))`)
    .eq("id", entryId)
    .single()
    .then(ok);

export const getScores = (entryId) =>
  supabase.from("hole_scores").select("hole_number, strokes")
    .eq("entry_id", entryId).order("hole_number").then(ok);

/** Eighteen holes in one call rather than eighteen round trips. */
export const saveCard = (entryId, scores) =>
  supabase.rpc("save_card", { p_entry_id: entryId, p_scores: scores }).then(ok);

export const submitCard = (entryId) =>
  supabase.rpc("submit_card", { p_entry_id: entryId }).then(ok);

export const reopenCard = (entryId) =>
  supabase.rpc("reopen_card", { p_entry_id: entryId }).then(ok);

export const roundLeaderboard = (roundId) =>
  supabase.from("v_round_leaderboard").select("*").eq("round_id", roundId).then(ok);

export const competitionStandings = (competitionId) =>
  supabase.from("v_competition_standings").select("*")
    .eq("competition_id", competitionId).then(ok);

/* ---------- competition board and player detail ---------- */

/** Every card in the competition, all rounds, in one read. */
export const boardForCompetition = (competitionId) =>
  supabase.from("v_round_leaderboard").select("*")
    .eq("competition_id", competitionId).then(ok);

/** One player's cards across the whole competition, holes and all. */
export const playerCards = (competitionId, profileId) =>
  supabase
    .from("round_entries")
    .select(`id, card_status, playing_handicap, course_handicap, handicap_index,
             rounds!inner(id, round_number, name, played_on, competition_id, courses(name)),
             tee_sets(name, par, tee_holes(hole_number, par, stroke_index)),
             hole_scores(hole_number, strokes)`)
    .eq("profile_id", profileId)
    .eq("rounds.competition_id", competitionId)
    .then(ok);

export const getProfile = (profileId) =>
  supabase.from("profiles").select("id, full_name, handicap_index, plays_from")
    .eq("id", profileId).single().then(ok);

/* ---------- announcements ---------- */

/** Society-wide posts, plus anything scoped to the given competition. */
export const listAnnouncements = (societyId, competitionId = null) => {
  let q = supabase
    .from("announcements")
    .select("*, profiles!announcements_author_id_fkey(full_name)")
    .eq("society_id", societyId)
    .order("pinned", { ascending: false })
    .order("created_at", { ascending: false });
  if (competitionId) q = q.or(`competition_id.is.null,competition_id.eq.${competitionId}`);
  else q = q.is("competition_id", null);
  return q.then(ok);
};

export const postAnnouncement = (societyId, authorId, { title, body, pinned, competitionId }) =>
  supabase.from("announcements").insert({
    society_id: societyId, author_id: authorId,
    competition_id: competitionId ?? null,
    title: title.trim(), body: body.trim(), pinned: !!pinned,
  }).select().single().then(ok);

export const deleteAnnouncement = (id) =>
  supabase.from("announcements").delete().eq("id", id).then(ok);

/* ---------- a player's own standing ---------- */

export const myCompetition = (competitionId, profileId) =>
  supabase.from("v_my_competition").select("*")
    .eq("competition_id", competitionId).eq("profile_id", profileId)
    .maybeSingle().then(ok);

export const myStanding = (competitionId, profileId) =>
  supabase.from("v_competition_standings").select("*")
    .eq("competition_id", competitionId).eq("profile_id", profileId)
    .maybeSingle().then(ok);

/** Every round of a competition with this player's card status attached. */
export const myRounds = (competitionId, profileId) =>
  supabase
    .from("round_entries")
    .select(`id, card_status, playing_handicap,
             rounds!inner(id, round_number, name, played_on, status, competition_id, courses(name))`)
    .eq("profile_id", profileId)
    .eq("rounds.competition_id", competitionId)
    .then(ok);

/* ---------- joining ---------- */

export const joinSociety = (code) =>
  supabase.rpc("join_society", { p_code: code }).single().then(ok);

export const resetJoinCode = (societyId) =>
  supabase.rpc("reset_join_code", { p_society_id: societyId }).then(ok);

/* ---------- invitations ---------- */

/** Everyone who may create an account for this society. */
export const listInvites = (societyId) =>
  supabase.from("signup_allowlist").select("*")
    .eq("society_id", societyId).order("created_at", { ascending: false }).then(ok);

export const inviteEmail = (societyId, email) =>
  supabase.rpc("invite_email", { p_society_id: societyId, p_email: email }).then(ok);

export const withdrawInvite = (societyId, email) =>
  supabase.rpc("withdraw_invite", { p_society_id: societyId, p_email: email }).then(ok);

/** Whether this society lets people sign up with its code alone. */
export const setCodeSignup = (societyId, allow) =>
  supabase.from("societies").update({ allow_code_signup: allow })
    .eq("id", societyId).select().single().then(ok);

/* ---------- roles ---------- */

export const setMemberRole = (societyId, profileId, role) =>
  supabase.rpc("set_member_role", {
    p_society_id: societyId, p_profile_id: profileId, p_role: role,
  }).single().then(ok);

export const deleteSociety = (societyId, confirmName) =>
  supabase.rpc("delete_society", {
    p_society_id: societyId, p_confirm_name: confirmName,
  }).then(ok);

/* ---------- platform admin ---------- */

export const allSocieties = () =>
  supabase.from("societies")
    .select("*, society_members(count), competitions(count)")
    .order("created_at", { ascending: false }).then(ok);

export const listPlatformAdmins = () =>
  supabase.from("platform_admins")
    .select("*, profiles!platform_admins_profile_id_fkey(full_name, email)").then(ok);

/** Which of this society's competitions a person is actually entered in. */
export const myCompetitionIds = (societyId, profileId) =>
  supabase
    .from("competition_entries")
    .select("competition_id, competitions!inner(society_id)")
    .eq("profile_id", profileId)
    .eq("competitions.society_id", societyId)
    .eq("withdrawn", false)
    .then(ok)
    .then((rows) => new Set(rows.map((r) => r.competition_id)));

/* ---------- renaming ---------- */

export const renameSociety = (societyId, name) =>
  supabase.rpc("rename_society", { p_society_id: societyId, p_name: name })
    .single().then(ok);

export const updateCompetition = (competitionId, { name, status = null }) =>
  supabase.rpc("update_competition", {
    p_competition_id: competitionId, p_name: name, p_status: status,
  }).single().then(ok);

export const renameRound = (roundId, name) =>
  supabase.rpc("rename_round", { p_round_id: roundId, p_name: name })
    .single().then(ok);

/** Frozen handicap indexes for everyone in a competition. */
export const listCompetitionEntries = (competitionId) =>
  supabase.from("competition_entries")
    .select("profile_id, handicap_index, withdrawn")
    .eq("competition_id", competitionId).eq("withdrawn", false).then(ok);

/* ---------- invitations that actually send ---------- */

/** Who's been told, who has an account. One read for the roster. */
export const memberInvites = (societyId) =>
  supabase.from("v_member_invites").select("*").eq("society_id", societyId).then(ok);

/**
 * Sends the invitation. The Resend key lives on the Edge Function, never
 * in the browser — which is the reason this isn't a plain fetch from here.
 */
export const sendInvite = async (societyId, profileId) => {
  const { data, error } = await supabase.functions.invoke("invite-member", {
    body: { society_id: societyId, profile_id: profileId },
  });
  if (error) {
    // functions.invoke buries the function's own message in the response
    let detail = "";
    try { detail = (await error.context?.json())?.error ?? ""; } catch { /* keep the generic one */ }
    throw new Error(detail || error.message ||
      "Couldn't send the invitation. Check the function is deployed.");
  }
  if (data?.error) throw new Error(data.error);
  return data;
};

/* ---------- the swindle ---------- */

export const getSwindle = (competitionId) =>
  supabase.from("swindles").select("*").eq("competition_id", competitionId)
    .maybeSingle().then(ok);

/** Creates the swindle. No prizes unless you ask for the starter set. */
export const enableSwindle = (competitionId, buyIn, seed = false) =>
  supabase.rpc("enable_swindle", {
    p_competition_id: competitionId, p_buy_in: Number(buyIn), p_seed: seed,
  }).single().then(ok);

/** Adds a chosen set. Returns how many were created; duplicates are skipped. */
export const addPrizes = (competitionId, specs) =>
  supabase.rpc("add_swindle_prizes", {
    p_competition_id: competitionId, p_specs: specs,
  }).then(ok);

export const disableSwindle = (competitionId) =>
  supabase.rpc("disable_swindle", { p_competition_id: competitionId }).then(ok);

export const listPrizes = (competitionId) =>
  supabase.from("swindle_prizes").select("*")
    .eq("competition_id", competitionId).order("sort_order").then(ok);

export const savePrize = (prize) =>
  prize.id
    ? supabase.from("swindle_prizes").update({
        label: prize.label, kind: prize.kind, holes: prize.holes,
        amount: Number(prize.amount) || 0, round_id: prize.round_id ?? null,
        ace_takes_pot: !!prize.ace_takes_pot,
      }).eq("id", prize.id).select().single().then(ok)
    : supabase.from("swindle_prizes").insert({
        competition_id: prize.competition_id, round_id: prize.round_id ?? null,
        label: prize.label, kind: prize.kind, holes: prize.holes,
        amount: Number(prize.amount) || 0, sort_order: prize.sort_order ?? 500,
        ace_takes_pot: !!prize.ace_takes_pot,
      }).select().single().then(ok);

export const deletePrize = (id) =>
  supabase.from("swindle_prizes").delete().eq("id", id).then(ok);

/** Winners and payouts, worked out from the cards. */
export const swindleResults = (competitionId) =>
  supabase.from("v_swindle_results").select("*")
    .eq("competition_id", competitionId).order("sort_order").then(ok);

export const swindlePot = (competitionId) =>
  supabase.from("v_swindle_pot").select("*")
    .eq("competition_id", competitionId).maybeSingle().then(ok);

export const listSwindleEntries = (competitionId) =>
  supabase.from("swindle_entries").select("*, profiles(full_name)")
    .eq("competition_id", competitionId).then(ok);

export const setSwindleEntry = (competitionId, profileId, paid) =>
  supabase.from("swindle_entries").upsert({
    competition_id: competitionId, profile_id: profileId,
    paid, paid_at: paid ? new Date().toISOString() : null,
  }).select().single().then(ok);

export const removeSwindleEntry = (competitionId, profileId) =>
  supabase.from("swindle_entries").delete()
    .eq("competition_id", competitionId).eq("profile_id", profileId).then(ok);

/* ---------- who's in the competition ---------- */

/** Every member of the society, with whether they're in this competition. */
export const competitionField = (competitionId) =>
  supabase.from("v_competition_field").select("*")
    .eq("competition_id", competitionId).order("full_name").then(ok);

export const enterPlayer = (competitionId, profileId) =>
  supabase.rpc("enter_player", {
    p_competition_id: competitionId, p_profile_id: profileId,
  }).single().then(ok);

/** Returns 'withdrawn' (cards kept) or 'removed' (no cards, taken out). */
export const withdrawPlayer = (competitionId, profileId) =>
  supabase.rpc("withdraw_player", {
    p_competition_id: competitionId, p_profile_id: profileId,
  }).then(ok);

/** Entered? Card waiting? Anything played? Three questions, one read. */
export const myCompetitionState = (competitionId, profileId) =>
  supabase.from("v_my_competition_state").select("*")
    .eq("competition_id", competitionId).eq("profile_id", profileId)
    .maybeSingle().then(ok);

/* ---------- deleting ---------- */

export const deleteCompetition = (competitionId, confirmName) =>
  supabase.rpc("delete_competition", {
    p_competition_id: competitionId, p_confirm_name: confirmName,
  }).then(ok);

/** Refuses when cards are in unless force is true — the message says how many. */
export const deleteRound = (roundId, force = false) =>
  supabase.rpc("delete_round", { p_round_id: roundId, p_force: force }).then(ok);

/** Wipes the holes and leaves a blank card. Returns how many were cleared. */
export const clearCard = (entryId) =>
  supabase.rpc("clear_card", { p_entry_id: entryId }).then(ok);

/** Takes the player off this round entirely; they stay in the competition. */
export const removeFromRound = (entryId) =>
  supabase.rpc("remove_from_round", { p_entry_id: entryId }).then(ok);

/* ---------- handicap history ---------- */

export const handicapHistory = (profileId) =>
  supabase.from("v_handicap_history").select("*")
    .eq("profile_id", profileId).order("changed_at").then(ok);

/** For a live competition: bring unplayed cards up to today's indexes. */
export const refreshRoundHandicaps = (roundId) =>
  supabase.rpc("refresh_round_handicaps", { p_round_id: roundId }).then(ok);

/**
 * Each player's course handicap across a competition's rounds.
 *
 * It isn't one number — it depends on the course's slope and rating, so a
 * four-round competition on two courses gives a player two figures. The
 * caller decides how to present that.
 */
export const competitionHandicaps = (competitionId) =>
  supabase.from("round_entries")
    .select("profile_id, course_handicap, playing_handicap, rounds!inner(competition_id)")
    .eq("rounds.competition_id", competitionId).then(ok);

/** This member's standing in every competition they're entered in. */
export const myStandings = (profileId) =>
  supabase.from("v_competition_standings").select("*")
    .eq("profile_id", profileId).then(ok);

/* ---------- tee times ---------- */

export const getTeeSheet = (roundId) =>
  supabase.from("tee_sheets").select("*").eq("round_id", roundId).maybeSingle().then(ok);

/** Every group and player for a round, flat — the caller groups them. */
export const teeSheetRows = (roundId) =>
  supabase.from("v_tee_sheet").select("*")
    .eq("round_id", roundId).order("group_number").order("position").then(ok);

export const myTeeTime = (roundId) =>
  supabase.from("v_my_tee_time").select("*").eq("round_id", roundId).maybeSingle().then(ok);

export const setTeeSheet = (roundId, { firstTee, interval, size, notes }) =>
  supabase.rpc("set_tee_sheet", {
    p_round_id: roundId, p_first_tee: firstTee,
    p_interval: Number(interval), p_size: Number(size), p_notes: notes ?? null,
  }).single().then(ok);

/** Places everyone not already in a group. Returns how many it placed. */
export const drawTeeTimes = (roundId, order = "random") =>
  supabase.rpc("draw_tee_times", { p_round_id: roundId, p_order: order }).then(ok);

export const moveToGroup = (groupId, profileId) =>
  supabase.rpc("move_to_group", { p_group_id: groupId, p_profile_id: profileId }).then(ok);

export const clearTeeTimes = (roundId) =>
  supabase.rpc("clear_tee_times", { p_round_id: roundId }).then(ok);

/** Posts the draw and pins a notice. */
export const postTeeTimes = (roundId) =>
  supabase.rpc("post_tee_times", { p_round_id: roundId }).then(ok);

/* ---------- moving people around the draw ---------- */

/** Appends a group, or inserts one after the given group number. */
export const addTeeGroup = (roundId, after = null) =>
  supabase.rpc("add_tee_group", { p_round_id: roundId, p_after: after })
    .single().then(ok);

/** Its players go back to "not drawn yet" rather than disappearing. */
export const removeTeeGroup = (groupId) =>
  supabase.rpc("remove_tee_group", { p_group_id: groupId }).then(ok);

export const removeFromDraw = (roundId, profileId) =>
  supabase.rpc("remove_from_draw", { p_round_id: roundId, p_profile_id: profileId }).then(ok);

/** Takes this group out of the automatic sequence. */
export const setGroupTime = (groupId, at) =>
  supabase.rpc("set_group_time", { p_group_id: groupId, p_at: at }).single().then(ok);

/** Every hole in one the society has recorded. */
export const holesInOne = (societyId) =>
  supabase.from("v_holes_in_one").select("*")
    .eq("society_id", societyId).order("played_on", { ascending: false }).then(ok);
